import type { ReactNode } from "react";
import { Logo } from "./Logo";

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Centered auth card on a dark, ambient-glow background.
 */
export function AuthLayout({ title, subtitle, children, footer }: Props) {
  return (
    <div className="relative flex min-h-screen flex-col px-4 py-10">
      {/* Concentrated brand glow above the card — dark only; on light it
          washes out and adds nothing. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 hidden h-[420px] bg-auth-glow dark:block"
      />
      <div className="relative mx-auto flex w-full max-w-[400px] flex-1 flex-col justify-center">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-6 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/70 dark:shadow-card dark:backdrop-blur-sm dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
          <header className="mb-5">
            <h1 className="text-[17px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1 text-[13px] text-zinc-600 dark:text-zinc-400">{subtitle}</p>
            )}
          </header>
          {children}
        </div>
        {footer && (
          <div className="mt-5 text-center text-[13px] text-zinc-600 dark:text-zinc-400">
            {footer}
          </div>
        )}
      </div>
      <footer className="relative mt-10 text-center text-xs text-zinc-500 dark:text-zinc-600">
        © {new Date().getFullYear()} TwilioPulse · Built for Twilio observability
      </footer>
    </div>
  );
}
